import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import SiteShell from '../components/site/SiteShell'
import { E164, FALLBACK_COUNTRIES, countriesFromApi, reconcileSelection, toE164 } from '../lib/countries'
import { codeComplete, countdownLabel } from '../lib/otp'
import { revealStep } from '../lib/reveal'
import { PRESS_EMAIL, SUPPORT_EMAIL, contactFailureKind, contactMailto } from '../lib/site'
import { fetchCountries } from '../services/auth'
import { requestContactCode, sendContact, sendMyContact } from '../services/contact'
import { useAuthStore } from '../store/authStore'
import { CodeField, PhoneField } from './Auth'

/**
 * Contact (`/contact`, design "Tooti Web" `contact`): the form, the
 * support mailbox, the response hours.
 *
 * The message is sent only from a phone the visitor proved they hold: they
 * pick a country and type the number (the sign-in's own picker and code
 * boxes), a code is texted to it, and the code travels with the message.
 * That is what tells a person from a script and makes the number a real way
 * to answer. The page says "sent" only on the server's 200 — when it did
 * not go through, the visitor gets a `mailto:` of the same text instead
 * (see `lib/site.js`). The design's social card carries placeholder
 * handles by its own note and is left out until real ones exist.
 */

const FIELD =
  'h-[58px] rounded-2xl border-2 border-line-2 bg-ground px-4 text-[15px] text-ink outline-none transition placeholder:text-ink-3 focus:border-brand-500'
const LABEL = 'text-[13.5px] font-bold text-ink-2'
const INFO_CARD = 'flex flex-col gap-2.5 rounded-3xl border-2 border-line bg-surface p-[22px] shadow-[0_6px_0_var(--color-line)]'
/*
 * The sign-in's own primary button (AuthFrame): the darker green, white on
 * good-deep is 4.0:1 light / 4.3:1 dark, over the 3:1 a large-text button
 * needs. Busy keeps the colour (aria-busy) instead of turning grey.
 */
const PRIMARY =
  'site-btn inline-flex h-14 w-full items-center justify-center gap-2 rounded-[18px] bg-good-deep font-display text-[18px] font-extrabold text-white [--press:color-mix(in_srgb,var(--color-good-deep)_62%,black)] disabled:cursor-not-allowed disabled:opacity-55 aria-busy:opacity-100'
const SECONDARY =
  'h-[52px] shrink-0 rounded-2xl border-2 border-line bg-surface px-4 font-display text-[15px] font-bold text-ink-2 transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:text-ink-3'

/** The visitor's fixed resend pace; the server is the authority on the real limits. */
const RESEND_SECONDS = 60

export default function Contact() {
  const { t, i18n } = useTranslation('site')
  const language = i18n.resolvedLanguage ?? i18n.language
  // A signed-in learner already holds a verified phone: no number, no code, no honeypot,
  // and the server answers on the account's own number (`contact_sendMine`).
  const user = useAuthStore((s) => s.user)
  const signedIn = useAuthStore((s) => s.status) === 'authenticated'
  const subjects = t('contact.subjects', { returnObjects: true })
  const hours = t('contact.hours', { returnObjects: true })
  // `website` is the honeypot: hidden from people, filled by form-scraping bots.
  const [form, setForm] = useState({ name: '', email: '', subject: subjects[0] ?? '', message: '', website: '' })
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES)
  const [country, setCountry] = useState(FALLBACK_COUNTRIES[0])
  const [localNumber, setLocalNumber] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [codeBusy, setCodeBusy] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  // null, or the key of the `contact.*` string that says what went wrong with the phone/code step.
  const [phoneError, setPhoneError] = useState(null)
  // 'idle' | 'sending' | 'sent' | 'mailFallback' | 'rateLimited' | 'failed' | 'otpInvalid'
  const [status, setStatus] = useState('idle')

  const codeRef = useRef(null)
  const codeStepRef = useRef(null)
  const phoneRef = useRef(null)
  const phone = signedIn ? (user?.phone ?? '') : toE164(country.callingCode, localNumber)

  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }))

  useEffect(() => {
    if (cooldown <= 0) return undefined
    const timer = setInterval(() => setCooldown((value) => value - 1), 1000)
    return () => clearInterval(timer)
  }, [cooldown])

  // The code step opens below the phone field, often off screen: bring it
  // into view and focus its boxes once it is actually rendered (a timeout
  // right after the state change can run before the boxes exist).
  useEffect(() => {
    if (codeSent) revealStep(codeStepRef.current, codeRef.current)
  }, [codeSent])

  // The real list replaces the fallback once it arrives; a failed or unusable
  // answer keeps the fallback, so the form never depends on it.
  useEffect(() => {
    if (signedIn) return undefined
    let cancelled = false
    fetchCountries()
      .then((items) => {
        const list = countriesFromApi(items)
        if (cancelled || !list) return
        setCountries(list)
        setCountry((current) => reconcileSelection(current, list))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [signedIn])

  /** The page's own fallback: what it did before the endpoint existed, and what it offers when delivery fails. */
  function openMail() {
    window.location.href = contactMailto({ ...form, reply: form.email.trim() || phone })
  }

  async function sendCode() {
    if (!E164.test(phone)) {
      setPhoneError('invalidPhone')
      return
    }
    setCodeBusy(true)
    setPhoneError(null)
    setStatus('idle')
    try {
      await requestContactCode(phone, form.website)
      setCodeSent(true)
      setCode('')
      setCooldown(RESEND_SECONDS)
      setStatus('idle')
    } catch (error) {
      const kind = contactFailureKind(error)
      if (kind === 'mailFallback') {
        // No bot on the server yet (and so no SMS was paid for): the mail client, as before.
        openMail()
        setStatus('mailFallback')
      } else {
        setPhoneError(kind === 'failed' ? 'codeFailed' : kind)
      }
    } finally {
      setCodeBusy(false)
    }
  }

  function changeNumber() {
    setCodeSent(false)
    setCode('')
    setPhoneError(null)
    setStatus('idle')
  }

  async function submitMine() {
    setStatus('sending')
    try {
      await sendMyContact(form)
      // Cleared so a second tap on the button cannot deliver the same message twice.
      setForm((prev) => ({ ...prev, message: '' }))
      setStatus('sent')
    } catch (error) {
      const kind = contactFailureKind(error)
      if (kind === 'mailFallback') openMail()
      setStatus(kind === 'rateLimited' || kind === 'mailFallback' ? kind : 'failed')
    }
  }

  async function submit(event) {
    event.preventDefault()
    if (signedIn) {
      if (status !== 'sending' && form.message.trim()) await submitMine()
      return
    }
    if (status === 'sending' || !codeSent || !codeComplete(code)) return
    setStatus('sending')
    try {
      await sendContact({ ...form, phone, code })
      // Cleared so a second tap on the button cannot deliver the same message twice.
      setForm((prev) => ({ ...prev, message: '' }))
      setCode('')
      setCodeSent(false)
      setStatus('sent')
    } catch (error) {
      const kind = contactFailureKind(error)
      if (kind === 'mailFallback') openMail()
      if (kind === 'otpInvalid') {
        setCode('')
        codeRef.current?.focus()
      }
      setStatus(kind === 'otpThrottled' || kind === 'countryNotLive' ? 'failed' : kind)
    }
  }

  const sending = status === 'sending'

  return (
    <SiteShell title={t('pageTitle.contact')}>
      <div className="mx-auto flex w-full max-w-[1020px] flex-col gap-[30px] px-4 pb-[clamp(44px,8vw,72px)] pt-[clamp(30px,6vw,54px)] sm:px-5">
        <header className="flex max-w-[54ch] flex-col gap-3.5" style={{ animation: 't-rise .5s ease-out both' }}>
          <span className="self-start rounded-full bg-coral-tint px-4 py-2 font-display text-[15px] font-extrabold text-coral-deep">{t('contact.badge')}</span>
          <h1 className="font-display text-[clamp(30px,6.4vw,44px)] font-extrabold leading-[1.15] tracking-[-1.3px]">{t('contact.title')}</h1>
          <p className="text-[17px] leading-[1.85] text-ink-2 [text-wrap:pretty]">{t('contact.lead')}</p>
        </header>

        <div className="grid items-start gap-[22px] [grid-template-columns:repeat(auto-fit,minmax(min(290px,100%),1fr))]">
          <form
            onSubmit={submit}
            className="flex min-w-0 flex-col gap-4 rounded-[28px] border-2 border-line bg-surface p-5 shadow-[0_8px_0_var(--color-line)] sm:p-7"
          >
            {!signedIn && (
              <div className="flex flex-col gap-2">
                <label htmlFor="contact-name" className={LABEL}>
                  {t('contact.name')}
                </label>
                <input id="contact-name" type="text" maxLength={80} value={form.name} onChange={update('name')} placeholder={t('contact.namePlaceholder')} className={FIELD} />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-subject" className={LABEL}>
                {t('contact.subject')}
              </label>
              <select id="contact-subject" value={form.subject} onChange={update('subject')} className={`${FIELD} px-3.5`}>
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-message" className={LABEL}>
                {t('contact.message')}
              </label>
              <textarea
                id="contact-message"
                rows={5}
                required
                maxLength={2000}
                value={form.message}
                onChange={update('message')}
                placeholder={t('contact.messagePlaceholder')}
                className="resize-y rounded-2xl border-2 border-line-2 bg-ground p-4 text-[15px] leading-[1.85] text-ink outline-none transition placeholder:text-ink-3 focus:border-brand-500"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-email" className={LABEL}>
                {t('contact.email')}
              </label>
              <input
                id="contact-email"
                dir="ltr"
                type="email"
                autoComplete="email"
                maxLength={120}
                value={form.email}
                onChange={update('email')}
                placeholder={t('contact.emailPlaceholder')}
                className={`${FIELD} text-start`}
              />
            </div>

            {/* The verified phone: a country, a number, a code. Stacked on a phone (the number would be clipped beside the country), side by side — reversed only under RTL, as on sign-in — from `sm`. */}
            {!signedIn && (
              <div className="flex flex-col gap-2 rounded-2xl border-2 border-line bg-ground p-4">
                <label htmlFor="contact-phone" className={LABEL}>
                  {t('contact.phone')}
                </label>
                <PhoneField
                  id="contact-phone"
                  errorId="contact-phone-error"
                  country={country}
                  countries={countries}
                  value={localNumber}
                  onChange={setLocalNumber}
                  onPick={setCountry}
                  invalid={Boolean(phoneError)}
                  disabled={codeSent}
                  inputRef={phoneRef}
                />

                {!codeSent ? (
                  <button type="button" onClick={sendCode} disabled={codeBusy} aria-busy={codeBusy || undefined} className={SECONDARY}>
                    {codeBusy ? t('contact.sendingCode') : t('contact.sendCode')}
                  </button>
                ) : (
                  <div ref={codeStepRef} className="flex flex-col gap-3 pt-1">
                    <p className="text-[13.5px] leading-[1.8] text-ink-2">
                      {t('contact.codeSentTo')}{' '}
                      <span dir="ltr" className="tnum font-bold text-ink">
                        {phone}
                      </span>
                    </p>
                    <CodeField code={code} onChange={setCode} inputRef={codeRef} disabled={sending} label={t('contact.codeLabel')} invalid={status === 'otpInvalid'} />
                    <div className="flex items-center justify-between gap-2.5">
                      {cooldown > 0 ? (
                        <span className="text-[13px] text-ink-2">{t('contact.resendIn', { countdown: countdownLabel(cooldown, language) })}</span>
                      ) : (
                        <button type="button" disabled={codeBusy} onClick={sendCode} className="font-display text-[15px] font-bold text-brand-600 disabled:text-ink-3">
                          {t('contact.resend')}
                        </button>
                      )}
                      <button type="button" onClick={changeNumber} className="font-display text-[15px] font-bold text-brand-600">
                        {t('contact.changeNumber')}
                      </button>
                    </div>
                  </div>
                )}

                {phoneError && (
                  <p id="contact-phone-error" role="alert" className="text-[13px] leading-[1.7] text-bad">
                    {t(`contact.${phoneError}`, { email: SUPPORT_EMAIL })}
                  </p>
                )}
              </div>
            )}

            {/* Honeypot: visually hidden, out of the tab order and the accessibility tree, so only a bot fills it. */}
            {!signedIn && (
              <div aria-hidden="true" className="sr-only">
                <label htmlFor="contact-website">Website</label>
                <input id="contact-website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update('website')} />
              </div>
            )}

            <button
              type="submit"
              disabled={sending || (signedIn ? !form.message.trim() : !codeSent || !codeComplete(code))}
              aria-busy={sending || undefined}
              className={PRIMARY}
            >
              {sending ? t('contact.sending') : t('contact.send')}
            </button>

            {status === 'sent' && (
              <p role="status" className="font-display text-base font-extrabold leading-[1.8] text-good-ink" style={{ animation: 't-pop .4s ease-out both' }}>
                {t('contact.sent')}
              </p>
            )}
            {status === 'mailFallback' && (
              <p role="status" className="font-display text-base font-extrabold leading-[1.8] text-good-ink" style={{ animation: 't-pop .4s ease-out both' }}>
                {t('contact.mailOpened', { email: SUPPORT_EMAIL })}
              </p>
            )}
            {status === 'otpInvalid' && (
              <p id="auth-error" role="alert" className="text-[15px] font-bold leading-[1.8] text-bad">
                {t('contact.wrongCode')}
              </p>
            )}
            {status === 'rateLimited' && (
              <p role="alert" className="text-[15px] font-bold leading-[1.8] text-ink">
                {t('contact.rateLimited')}
              </p>
            )}
            {status === 'failed' && (
              <div role="alert" className="flex flex-col gap-2 text-[15px] font-bold leading-[1.8] text-ink">
                <p>{t('contact.failed', { email: SUPPORT_EMAIL })}</p>
                <a href={contactMailto({ ...form, reply: form.email.trim() || phone })} className="text-start text-brand-600 hover:underline">
                  {t('contact.viaEmail')}
                </a>
              </div>
            )}
          </form>

          <div className="flex min-w-0 flex-col gap-4">
            <section className={INFO_CARD}>
              <h2 className="font-display text-xl font-extrabold">{t('contact.emailTitle')}</h2>
              <a href={`mailto:${SUPPORT_EMAIL}`} dir="ltr" className="text-start text-base font-bold text-brand-600 hover:underline">
                {SUPPORT_EMAIL}
              </a>
              <span className="text-sm leading-[1.8] text-ink-2">
                {t('contact.pressLine')}{' '}
                <a href={`mailto:${PRESS_EMAIL}`} dir="ltr" className="font-bold text-ink hover:underline">
                  {PRESS_EMAIL}
                </a>
              </span>
            </section>
            <section className={INFO_CARD}>
              <h2 className="font-display text-xl font-extrabold">{t('contact.hoursTitle')}</h2>
              {hours.map((row) => (
                <div key={row.days} className="flex justify-between gap-2.5 text-[14.5px]">
                  <span className="text-ink-2">{row.days}</span>
                  <span dir="ltr" className="tnum font-bold">
                    {row.time}
                  </span>
                </div>
              ))}
              <div className="flex justify-between gap-2.5 text-[14.5px] text-ink-2">
                <span>{t('contact.friday')}</span>
                <span>{t('contact.closed')}</span>
              </div>
              <span className="text-[13px] leading-[1.85] text-ink-2">{t('contact.hoursNote')}</span>
            </section>
          </div>
        </div>
      </div>
    </SiteShell>
  )
}
